import React from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    alpha,
    Box,
    IconButton,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import moment from "moment";

import type { StayDetail } from "../../services/restserver-openapi/models/StayDetail";

const CUSTOM_STAY_TYPE = {
    HOST: "HOST" as StayDetail.stayType,
    CUSTOM_START: "CUSTOM_START" as StayDetail.stayType,
    CUSTOM_STOP: "CUSTOM_STOP" as StayDetail.stayType,
    CUSTOM_END: "CUSTOM_END" as StayDetail.stayType,
};

type Props = {
    stay: StayDetail;
    lineColor: string;
    expanded: boolean;
    onToggle: () => void;
    canEdit: boolean;
    editDisabledTooltip: string;
    onEdit: () => void;
    onDelete: () => void;
    anonymized?: boolean;
    active?: boolean;
    hovered?: boolean;
    onHoverChange?: (hovered: boolean) => void;
};

function getTypeLabel(stayType?: StayDetail.stayType): string {
    switch (stayType) {
        case CUSTOM_STAY_TYPE.CUSTOM_START:
            return "Privater Startort";
        case CUSTOM_STAY_TYPE.CUSTOM_END:
            return "Privater Zielort";
        default:
            return "Privater Zwischenstopp";
    }
}

function showsDateRange(stayType?: StayDetail.stayType): boolean {
    return stayType !== CUSTOM_STAY_TYPE.CUSTOM_START && stayType !== CUSTOM_STAY_TYPE.CUSTOM_END;
}

export default function CustomStayTimelineItem({ stay, lineColor, expanded, onToggle, canEdit, editDisabledTooltip, onEdit, onDelete, anonymized = false, active = false, hovered = false, onHoverChange }: Props) {
    const theme = useTheme();
    const activeAccentColor = theme.palette.mode === 'light' ? theme.palette.secondary.main : theme.palette.primary.main;
    const hoverFrameColor = theme.palette.mode === 'light' ? theme.palette.grey[400] : theme.palette.grey[700];
    const title = anonymized
        ? "Privater Zwischenstopp"
        : stay.customTitle || stay.customLocationName || stay.customAddressLine1 || "Manueller Reisepunkt";
    const addressParts = [stay.customAddressLine1, stay.customAddressLine2, stay.customZipCode && stay.customCity ? `${stay.customZipCode} ${stay.customCity}` : stay.customZipCode || stay.customCity]
        .filter((value): value is string => !!value && value.trim().length > 0);

    return (
        <Box sx={{ display: "flex", mb: 3 }} onMouseEnter={() => onHoverChange?.(true)} onMouseLeave={() => onHoverChange?.(false)}>
            <Box sx={{ borderLeft: `3px solid ${lineColor}`, borderRadius: 2, mr: 2, width: "3px" }} />
            <Box sx={{ flex: 1 }}>
                <Accordion
                    disableGutters
                    elevation={0}
                    expanded={expanded}
                    onChange={onToggle}
                    sx={{
                        borderRadius: 2,
                        boxShadow: active
                            ? `0 0 0 2px ${alpha(activeAccentColor, 0.95)}`
                            : hovered
                                ? `0 0 0 2px ${alpha(hoverFrameColor, 0.95)}`
                                : 'none',
                        "&:before": { display: "none" },
                    }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "text.secondary" }} />}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                sx={{
                                    textTransform: "uppercase",
                                    fontWeight: 700,
                                    fontSize: 12,
                                    letterSpacing: 0.6,
                                }}
                                color="text.secondary"
                            >
                                {getTypeLabel(stay.stayType)}
                            </Typography>
                            <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
                            {!anonymized && addressParts.length > 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                    {addressParts.join(", ")}
                                </Typography>
                            )}
                            {showsDateRange(stay.stayType) && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                    {moment(stay.fromDate).format("DD.MM.YYYY")} - {moment(stay.toDate).format("DD.MM.YYYY")}
                                </Typography>
                            )}
                        </Box>
                        <Tooltip title={canEdit ? "Manuellen Reisepunkt bearbeiten" : editDisabledTooltip}>
                            <span>
                                <IconButton size="small" onClick={(event) => { event.stopPropagation(); onEdit(); }} disabled={!canEdit}>
                                    <EditRoundedIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={canEdit ? "Manuellen Reisepunkt entfernen" : editDisabledTooltip}>
                            <span>
                                <IconButton size="small" onClick={(event) => { event.stopPropagation(); onDelete(); }} disabled={!canEdit}>
                                    <DeleteOutlineRoundedIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </AccordionSummary>
                    <AccordionDetails>
                        {!anonymized && stay.customDescription ? (
                            <Typography variant="body2">{stay.customDescription}</Typography>
                        ) : (
                            <Typography variant="body2" color="text.secondary">{anonymized ? "Privater Zwischenstopp innerhalb der Reise." : "Keine zusätzlichen Informationen."}</Typography>
                        )}
                    </AccordionDetails>
                </Accordion>
            </Box>
        </Box>
    );
}
